import React, { useReducer } from 'react'
import { useMutation } from '@tanstack/react-query'
import Compressor from 'compressorjs'
import { useAppTranslation } from './useAppTranslation'
import { useToastNotifications } from './useToastNotification'
import { imageUploadReducer } from '../reducers/image-upload'
import { ToastType } from '../components/common/Toast/toast-type'
import { isImageType } from '../helpers/types'
import { ImageUploadState, ProcessedMedia } from '../types/image-upload'
import { ImageActions, ImageUploadActions } from '../actions/image-upload'
import { convertBase64 } from '../api/helpers'
import { createMedia, deleteMedia } from '../api/media'

const isServerId = (id: string | undefined): id is string =>
  !!id && !id.startsWith('blob:') && !id.startsWith('http')

export const useUploadMedia = (
  maxImageUploadLimit = 10
): {
  imageUploading: boolean
  onImageRemove: (image: ProcessedMedia) => void
  uploadImage: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>
  uploadFiles: (files: File[]) => void
  onImagePaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void
  imageUploadDisabled: boolean
  messageImagesState: ImageUploadState
  messageImagesDispatch: React.Dispatch<ImageUploadActions>
} => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { mutate: mutateDeleteMedia } = useMutation({ mutationFn: deleteMedia })
  const [messageImagesState, messageImagesDispatch] = useReducer(
    imageUploadReducer,
    {
      images: [],
    }
  )

  const imageUploadDisabled =
    messageImagesState.images.length >= maxImageUploadLimit

  const hasLimitExceeded = (imagesLength: number): boolean => {
    return (
      imagesLength > maxImageUploadLimit ||
      messageImagesState.images.length + imagesLength > maxImageUploadLimit
    )
  }

  const uploadFiles = (files: File[]): void => {
    if (files.length === 0) {
      return
    }
    if (hasLimitExceeded(files.length)) {
      setToastNotification(
        ToastType.DANGER,
        translate('chatUpload.flashMessage.uploadImageLimitReached')
      )
      return
    }
    const seenSignatures = new Set(
      messageImagesState.images.flatMap((image) =>
        image.signature ? [image.signature] : []
      )
    )
    files.forEach((file) => {
      if (!isImageType(file.type)) {
        setToastNotification(
          ToastType.DANGER,
          translate('uploadImage.invalidImageType')
        )
        return
      }

      const signature = `${file.name}-${file.size}-${file.type}`
      if (seenSignatures.has(signature)) {
        return
      }
      seenSignatures.add(signature)

      const uri = URL.createObjectURL(file)
      const _image: ProcessedMedia = { uri, id: uri, signature }
      messageImagesDispatch({ type: ImageActions.ADD, image: _image })

      // eslint-disable-next-line no-new
      new Compressor(file, {
        quality: 0.8, // 0.6 can also be used, but its not recommended to go below.
        success: async (result) => {
          try {
            const base64 = await convertBase64(result)
            const response = (await createMedia('image', base64 as string)).data
              .data
            if (!response) {
              throw new Error('createMedia returned no data')
            }
            messageImagesDispatch({
              type: ImageActions.SET_UPLOADED,
              mediaId: response._id as string,
              image: _image,
            })
          } catch {
            setToastNotification(
              ToastType.DANGER,
              translate('flashMessage.somethingWentWrongError')
            )
            messageImagesDispatch({ type: ImageActions.REMOVE, id: uri })
          }
        },
        error: () => {
          setToastNotification(
            ToastType.DANGER,
            translate('flashMessage.somethingWentWrongError')
          )
          messageImagesDispatch({ type: ImageActions.REMOVE, id: uri })
        },
      })
    })
  }

  const uploadImage = async (
    e: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const images = e.target.files
    if (images) {
      uploadFiles(Array.from(images))
    }
    e.target.value = ''
  }

  // Text wins: spreadsheet and document cells carry a PNG beside the text.
  const onImagePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>): void => {
    if (e.clipboardData?.types.includes('text/plain')) return
    const files = Array.from(e.clipboardData?.items ?? [])
      .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null)

    if (files.length === 0) return
    e.preventDefault()
    uploadFiles(files)
  }

  const onImageRemove = (image: ProcessedMedia): void => {
    if (isServerId(image.id)) {
      mutateDeleteMedia(image.id)
    }
    messageImagesDispatch({ type: ImageActions.REMOVE, id: image.id as string })
  }

  const checkImageUploading = (images: ProcessedMedia[]): boolean => {
    return images.map((image) => image.uploading).includes(true)
  }

  const imageUploading = checkImageUploading(messageImagesState.images)

  return {
    imageUploading,
    onImageRemove,
    uploadImage,
    uploadFiles,
    onImagePaste,
    imageUploadDisabled,
    messageImagesDispatch,
    messageImagesState,
  }
}
